import { useEffect, useState } from "react";
import {
  investors as investorsApi,
  type Investor,
  type QpClientFlow,
  type QpFlowOption,
} from "../../api";
import { InvestorCombobox } from "./InvestorCombobox";
import {
  embedApi,
  formatDate,
  statusClass,
  statusLabel,
  type EmbedSession,
  type EmbedState,
} from "./embedApi";

type Mode = "new" | "resume";

const STATE_PILL: Record<EmbedState, string> = {
  open: "fstatus-in_progress",
  closed: "fstatus-inactive",
  failed: "ext-canceled",
};

// Launcher: pick an investor, then start a new QP flow session or resume one of
// the investor's existing flow-instances. Either way the server mints the embed
// URL and the parent switches to the session view.
export function EmbedLauncher({
  initialInvestorId,
  onOpen,
}: {
  initialInvestorId: number | null;
  onOpen: (session: EmbedSession) => void;
}) {
  const [investors, setInvestors] = useState<Investor[] | null>(null);
  const [flows, setFlows] = useState<QpFlowOption[] | null>(null);
  const [investorId, setInvestorId] = useState<number | "">(initialInvestorId ?? "");
  const [mode, setMode] = useState<Mode>("new");
  const [flowId, setFlowId] = useState("");
  const [instances, setInstances] = useState<QpClientFlow[] | null>(null);
  const [linked, setLinked] = useState(true);
  const [recent, setRecent] = useState<EmbedSession[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    investorsApi.list().then((d) => setInvestors(d.investors)).catch((e) => setErr(e.message));
    embedApi.flows().then((d) => setFlows(d.flows)).catch((e) => setErr(e.message));
  }, []);

  // Default to the tenant's Investment Advice flow once the catalog loads.
  useEffect(() => {
    if (flows && !flowId) {
      const adv = flows.find((f) => /investment advice/i.test(f.name)) ?? flows[0];
      if (adv) setFlowId(adv.id);
    }
  }, [flows, flowId]);

  // Resume list + recent sessions follow the selected investor. The `stale`
  // flag drops responses for an investor the user has already moved away from.
  useEffect(() => {
    let stale = false;
    const id = investorId === "" ? undefined : investorId;
    embedApi
      .sessions(id)
      .then((d) => !stale && setRecent(d.sessions))
      .catch(() => {});
    setInstances(null);
    setLinked(true);
    if (id !== undefined) {
      embedApi
        .instances(id)
        .then((d) => {
          if (stale) return;
          setLinked(d.linked);
          setInstances(d.instances);
        })
        .catch((e) => !stale && setErr((e as Error).message));
    }
    return () => {
      stale = true;
    };
  }, [investorId]);

  async function start(body: { flowId: string } | { flowInstanceId: string }) {
    if (!investorId || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const { session } = await embedApi.start({ investorId: Number(investorId), ...body });
      onOpen(session);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  async function reopen(id: number) {
    try {
      const { session } = await embedApi.session(id);
      onOpen(session);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <span className="eyebrow">Embedded Flow</span>
          <h1>Work in Quantfolio</h1>
        </div>
      </header>
      <p className="muted">
        Open a Quantfolio flow session for an investor right here in Andersen —
        signed in through Andersen, no new tab and no second login.
      </p>

      {err && <div className="banner-error">{err}</div>}

      <div className="card-form">
        <div className="field">
          <span>Investor *</span>
          <InvestorCombobox investors={investors} value={investorId} onChange={setInvestorId} />
        </div>

        <div className="field">
          <span>Session</span>
          <div className="seg">
            <button
              type="button"
              className={mode === "new" ? "seg-option active" : "seg-option"}
              onClick={() => setMode("new")}
            >
              Start new
            </button>
            <button
              type="button"
              className={mode === "resume" ? "seg-option active" : "seg-option"}
              onClick={() => setMode("resume")}
            >
              Resume existing
            </button>
          </div>
        </div>

        {mode === "new" ? (
          <>
            <label className="field">
              <span>Flow *</span>
              <select
                value={flowId}
                onChange={(e) => setFlowId(e.target.value)}
                disabled={!flows}
              >
                {!flows && <option value="">Loading flows…</option>}
                {(flows ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => start({ flowId })}
              disabled={!investorId || !flowId || busy}
            >
              {busy ? "Opening…" : "Open in Quantfolio →"}
            </button>
          </>
        ) : (
          <ResumeList
            investorSelected={investorId !== ""}
            linked={linked}
            instances={instances}
            busy={busy}
            onResume={(id) => start({ flowInstanceId: id })}
          />
        )}
      </div>

      {recent.length > 0 && (
        <>
          <h2 className="section-title" style={{ marginTop: "var(--s-lg)" }}>
            Recent embedded sessions
          </h2>
          <table className="table">
            <thead>
              <tr>
                <th>Investor</th>
                <th>Flow</th>
                <th>QP status</th>
                <th>Session</th>
                <th>Opened</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {recent.map((s) => (
                <tr key={s.id} className="row-link" onClick={() => reopen(s.id)}>
                  <td className="strong">{s.investor_name ?? "—"}</td>
                  <td>{s.flow_name ?? "—"}</td>
                  <td>
                    {s.qp_status ? (
                      <span className={`pill ${statusClass(s.qp_status)}`}>
                        {statusLabel(s.qp_status)}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <span className={`pill ${STATE_PILL[s.state]}`}>{s.state}</span>
                  </td>
                  <td>{formatDate(s.created_at)}</td>
                  <td className="muted">Open →</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

function ResumeList({
  investorSelected,
  linked,
  instances,
  busy,
  onResume,
}: {
  investorSelected: boolean;
  linked: boolean;
  instances: QpClientFlow[] | null;
  busy: boolean;
  onResume: (flowInstanceId: string) => void;
}) {
  if (!investorSelected) {
    return <p className="muted">Select an investor to see their Quantfolio sessions.</p>;
  }
  if (instances === null) return <p className="muted">Loading sessions…</p>;
  if (!linked) {
    return (
      <p className="muted">
        This investor is not linked to Quantfolio yet — start a new session to link them.
      </p>
    );
  }
  if (instances.length === 0) {
    return <p className="muted">No Quantfolio sessions for this investor yet.</p>;
  }
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Session</th>
          <th>Status</th>
          <th>Started</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {instances.map((f) => (
          <tr
            key={f.id}
            className="row-link"
            onClick={() => !busy && onResume(f.id)}
            aria-disabled={busy}
          >
            <td className="strong">
              {f.name ?? "—"}
              {f.flow?.name && <div className="muted">{f.flow.name}</div>}
            </td>
            <td>
              <span className={`pill ${statusClass(f.status)}`}>{statusLabel(f.status)}</span>
              {f.externalStatus && (
                <span className={`pill ext-${f.externalStatus}`}>{f.externalStatus}</span>
              )}
            </td>
            <td>{formatDate(f.startedAt)}</td>
            <td className="muted">{busy ? "Opening…" : "Resume →"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
